import { Edit as EditIcon } from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import TableCard from 'lib/designSystem/Card/TableCard';
import React, { useEffect, useState } from 'react';

interface ObservationCardProps {
  observation: string;
  onObservationChange: (value: string) => void;
  onSave?: (value: string) => void | Promise<void>;
  canEdit?: boolean;
}

const ObservationCard: React.FC<ObservationCardProps> = ({
  observation,
  onObservationChange,
  onSave,
  canEdit = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localObservation, setLocalObservation] = useState(observation);

  useEffect(() => {
    if (!isEditing) {
      setLocalObservation(observation);
    }
  }, [observation, isEditing]);

  const handleEditClick = () => {
    setIsEditing(true);
    setLocalObservation(observation);
  };

  const handleSave = async () => {
    onObservationChange(localObservation);
    if (onSave) {
      await onSave(localObservation);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalObservation(observation);
    setIsEditing(false);
  };

  const handleChange = (value: string) => {
    setLocalObservation(value);
    if (isEditing) {
      onObservationChange(value);
    }
  };

  return (
    <TableCard
      size="medium"
      enabledEffects={false}
      sx={{
        boxShadow: 'none',
        borderColor: 'divider',
        background: 'transparent',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            color: 'text.primary',
            fontSize: '0.95rem',
            boxShadow: 'none',
          }}
        >
          Observación
        </Typography>
        {!isEditing && canEdit && (
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{
              backgroundColor: 'rgba(15, 35, 223, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(15, 35, 223, 0.16)',
              },
            }}
          >
            <EditIcon sx={{ fontSize: '0.85rem', color: 'primary.main' }} />
          </IconButton>
        )}
      </Box>
      <TextField
        multiline
        rows={3}
        fullWidth
        size="small"
        disabled={!isEditing}
        value={isEditing ? localObservation : observation}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={isEditing ? handleSave : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && isEditing) {
            handleCancel();
          }
        }}
        placeholder="Ingrese una observación..."
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            '& fieldset': {
              borderColor: 'divider',
            },
            '&:hover fieldset': {
              borderColor: isEditing ? 'primary.main' : 'divider',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              '& fieldset': {
                borderColor: 'divider',
              },
            },
          },
        }}
      />
    </TableCard>
  );
};

export default ObservationCard;
